import CustomFields from 'components/forms/inputs/custom-fields/CustomFields';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import { Row } from 'components/GridItemList';
import Loader from 'components/Loader';
import { SectionEdit, SectionEditForm } from 'components/section-edit/SectionEdit';
import { useFrozenOnceLoaded } from 'components/section-edit/useFrozenOnceLoaded';
import { saveSection } from 'components/section-edit/saveSection';
import { ReactNode } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useGetOfficeByIdQuery, useUpdateOfficeMutation } from 'store/api/offices';
import { ClassifierData } from 'types/classifiers';
import { CustomField } from 'types/customFields';
import { EntityType } from 'types/entity';
import { Office, OfficeForm } from 'types/offices';
import { useClassifiers } from 'utils/useClassifiers';
import { OFFICE_SECTION_FIELDS, OfficeSectionId, officeDtoToForm, officeFormToRequest } from '../officeFormMapping';
import {
    OfficeActiveField,
    OfficeContactFields,
    OfficeFields,
    OfficeInvoiceAbbreviationField,
    OfficeInvoicingFields,
    OfficeLocalAddressField,
} from './OfficeFields';
import { OFFICE_FORM_CLASSIFIERS, OfficeFormState, useOfficeForm } from './useOfficeForm';

const SECTION_FIELDS: Record<OfficeSectionId, (form: OfficeFormState) => ReactNode> = {
    office: (form) => (
        <>
            <OfficeFields form={form} />
            <OfficeActiveField form={form} />
        </>
    ),
    contact: (form) => (
        <>
            <OfficeContactFields form={form} />
            <OfficeLocalAddressField form={form} />
        </>
    ),
    invoicing: (form) => (
        <>
            <OfficeInvoiceAbbreviationField form={form} />
            <OfficeInvoicingFields form={form} />
        </>
    ),
    'custom-fields': (form) => (
        <CustomFields control={form.methods.control as unknown as Control<FieldValues>} fields={form.customFields} />
    ),
};

interface SectionFormProps {
    section: OfficeSectionId;
    initialData: OfficeForm;
    classifiers: Record<string, ClassifierData>;
    customFields: CustomField[];
    onClose: () => void;
}

function OfficeSectionForm({ section, initialData, classifiers, customFields, onClose }: SectionFormProps) {
    // The page's own query argument, so the refetch refreshes the office the page shows.
    const { id: officeId = '' } = useParams();
    const { refetch } = useGetOfficeByIdQuery(officeId);
    const [updateOffice] = useUpdateOfficeMutation();
    const form = useOfficeForm({ initialData, classifiers, entityCustomFields: customFields });

    const onSave = (values: OfficeForm) =>
        saveSection(values, {
            section,
            sectionFields: OFFICE_SECTION_FIELDS,
            fetchLatest: () => refetch().unwrap(),
            toForm: (office) => officeDtoToForm(office, form.customFieldValuesOf(office.customFields)),
            toRequest: (edited) =>
                officeFormToRequest(edited, form.mapCustomFieldsToDto(edited as unknown as Record<string, unknown>)),
            update: (request) => updateOffice({ ...request, id: officeId }).unwrap(),
            onSaved: onClose,
        });

    if (form.isLoading) {
        return <Loader />;
    }
    return (
        <SectionEditForm methods={form.methods} onSave={onSave} onCancel={onClose}>
            {SECTION_FIELDS[section](form)}
        </SectionEditForm>
    );
}

interface EditorProps {
    section: OfficeSectionId;
    office: Office;
    onClose: () => void;
}

function OfficeSectionEditor({ section, office, onClose }: EditorProps) {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: OFFICE_FORM_CLASSIFIERS });
    const {
        customFields,
        initialCustomFieldsObject,
        isLoading: isLoadingCustomFields,
    } = useCustomFields({ entityType: EntityType.OFFICE, fields: office.customFields });

    const initialData = useFrozenOnceLoaded(!isLoadingClassifiers && !isLoadingCustomFields, () =>
        officeDtoToForm(office, initialCustomFieldsObject)
    );

    if (!initialData) {
        return <Loader />;
    }
    return (
        <OfficeSectionForm
            section={section}
            initialData={initialData}
            classifiers={classifiers}
            customFields={customFields}
            onClose={onClose}
        />
    );
}

interface OfficeSectionProps {
    id: OfficeSectionId;
    titleId: string;
    office: Office;
    /** The read view, handed the card's Edit control. */
    children: (edit: ReactNode) => ReactNode;
    /** The read view's rows: those no field edits stay under the fields while the card edits. */
    rows?: Row[];
}

/** A card of the office page that edits in place with the office form's fields for that card. */
export function OfficeSection({ id, titleId, office, children, rows }: OfficeSectionProps) {
    return (
        <SectionEdit
            id={id}
            titleId={titleId}
            editor={(close) => <OfficeSectionEditor section={id} office={office} onClose={close} />}
            rows={rows}
        >
            {children}
        </SectionEdit>
    );
}
